import { Fragment, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { className } from '../../../../utils/GlobalCssProxy';

// template component imports
import AfterTaxAnnuity from './after-tax-annuity';
import AfterTaxAnnuityMoneyMarket from './after-tax-annuity-money-market';
import CollectiveInvestmentTrust from './cit';
import FdicInsuredBankProduct from './fdic-insured-bank-product';
import FixedAnnuityOther from './fixed-annuity-other';
import FixedAnnuityTiaaStableValue from './fixed-annuity-tiaa-stable-value';
import FixedAnnuityTiaaTraditional from './fixed-annuity-tiaa-traditional';
import Icon from '../../../atoms/icon';
import Loading from './loading';
import MutualFund from './mutual-fund';
import MutualFundMoneyMarket from './mutual-fund-money-market';
import MutualFundNonProp from './mutual-fund-non-prop';
import MutualFundNonPropMoneyMarket from './mutual-fund-non-prop-money-market';
import Other from './other';
import OtherMoneyMarket from './other-money-market';
import ProfileAppContext from '../../../../stores/profile/profile-app-context';
import RetirePlus from './retire-plus';
import FixedAnnuityTiaaQdia from './fixed-annuity-tiaa-qdia';
import VariableAnnuityCref from './variable-annuity-cref';
import VariableAnnuityCrefMoneyMarket from './variable-annuity-cref-money-market';
import VariableAnnuityOther from './variable-annuity-other';
import VariableAnnuityTiaaAccess from './variable-annuity-tiaa-access';
import VariableAnnuityTiaaAccessMoneyMarket from './variable-annuity-tiaa-access-money-market';
import VariableAnnuityTiaaAccessNonProp from './variable-annuity-tiaa-access-non-prop';
import VariableAnnuityTiaaRealEstate from './variable-annuity-tiaa-real-estate';
import adobeAnalytics from '../../../../utils/adobe-analytics';
import useProfileCommonStore from '../../../../stores/profile/use-profile-common-store';
import { TEMPLATES } from '../../../../stores/profile/get-profile-template';

const componentByTemplate = {
  [TEMPLATES.afterTaxAnnuity]: AfterTaxAnnuity,
  [TEMPLATES.afterTaxAnnuityMoneyMarket]: AfterTaxAnnuityMoneyMarket,
  [TEMPLATES.collectiveInvestmentTrust]: CollectiveInvestmentTrust,
  [TEMPLATES.fdicInsuredBankProduct]: FdicInsuredBankProduct,
  [TEMPLATES.fixedAnnuityOther]: FixedAnnuityOther,
  [TEMPLATES.fixedAnnuityTiaaQdia]: FixedAnnuityTiaaQdia,
  [TEMPLATES.fixedAnnuityTiaaStableValue]: FixedAnnuityTiaaStableValue,
  [TEMPLATES.fixedAnnuityTiaaTraditional]: FixedAnnuityTiaaTraditional,
  [TEMPLATES.loading]: Loading,
  [TEMPLATES.mutualFundMoneyMarket]: MutualFundMoneyMarket,
  [TEMPLATES.mutualFundNonPropMoneyMarket]: MutualFundNonPropMoneyMarket,
  [TEMPLATES.mutualFundNonProp]: MutualFundNonProp,
  [TEMPLATES.mutualFund]: MutualFund,
  [TEMPLATES.none]: Other,
  [TEMPLATES.otherMoneyMarket]: OtherMoneyMarket,
  [TEMPLATES.other]: Other,
  [TEMPLATES.retirePlus]: RetirePlus,
  [TEMPLATES.variableAnnuityCrefMoneyMarket]: VariableAnnuityCrefMoneyMarket,
  [TEMPLATES.variableAnnuityCref]: VariableAnnuityCref,
  [TEMPLATES.variableAnnuityOther]: VariableAnnuityOther,
  [TEMPLATES.variableAnnuityTiaaAccessMoneyMarket]: VariableAnnuityTiaaAccessMoneyMarket,
  [TEMPLATES.variableAnnuityTiaaAccessNonPropMoneyMarket]: VariableAnnuityTiaaAccessMoneyMarket,
  [TEMPLATES.variableAnnuityTiaaAccessNonProp]: VariableAnnuityTiaaAccessNonProp,
  [TEMPLATES.variableAnnuityTiaaAccess]: VariableAnnuityTiaaAccess,
  [TEMPLATES.variableAnnuityTiaaRealEstate]: VariableAnnuityTiaaRealEstate
};

const TemplateFactory = ({ templateOverride }) => {
  const { auxiliary, template } = useContext(ProfileAppContext);
  const { commonState = {} } = useProfileCommonStore();

  const channel = commonState.channel;
  const overriddenTemplate = template !== TEMPLATES.loading
    ? templateOverride || template
    : template;

  const TemplateComponent = componentByTemplate[overriddenTemplate] || Other;

  // Handles updating Adobe Analytics for the ProfileApp when hosted via a TIAA integrated environment.
  useEffect(() => {
    adobeAnalytics.setPageTags({ auxiliary, pageName: 'Profile' });
    if (jQuery && (channel === 'public' || channel === 'microsites')) {
      jQuery(document).trigger('analyticsload.tiaacref', '#com_tiaa_profile');
    }
  }); // Apply to each render

  if (channel === 'mytiaa') {
    let myTiaaBody = document.querySelector('body');
    if (myTiaaBody) {
      const myTiaaObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.attributeName !== 'unresolved') return; //when TIAA removes unresolved attr on <body>, trigger a resize event.
          window.dispatchEvent(new Event('resize'));
          myTiaaObserver.disconnect();
        })
      });
      myTiaaObserver.observe(myTiaaBody, { attributes: true });
    }
  }

  return <Fragment>
    <Icon.TIAALogo forPrint {...className('d-none', 'd-print-inline-block', 'print-tiaa-logo')} />
    <TemplateComponent />
  </Fragment>;
};

export default TemplateFactory;

TemplateFactory.propTypes = {
  templateOverride: PropTypes.string
};
